// GestionEvaluationsAdmin.jsx - Version avec bouton de validation du modèle et libération de cache
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { APP_CONFIG } from '../../config/appConfig';
import ConstruireEvaluation from './ConstruireEvaluation';
import ConstruireEvaluationRich from './ConstruireEvaluationRich';
import DemoViewer from '../../components/communs/DemoViewer';
import { Plus, Edit, Trash2, Eye, RefreshCw, Search, FileQuestion, Layers, Clock, CheckCircle } from 'lucide-react';

export default function GestionEvaluationsAdmin() {
    const { token } = useAuth();

    const [currentTab, setCurrentTab] = useState('classic');
    const [classicEvaluations, setClassicEvaluations] = useState([]);
    const [richEvaluations, setRichEvaluations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');

    const [classicPagination, setClassicPagination] = useState({ page: 0, size: 10, totalElements: 0, totalPages: 0 });
    const [richPagination, setRichPagination] = useState({ page: 0, size: 10, totalElements: 0, totalPages: 0 });

    const [selectedEvaluationId, setSelectedEvaluationId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [modalType, setModalType] = useState('classic');

    const [showEvalDemo, setShowEvalDemo] = useState(false);
    const [demoEvalData, setDemoEvalData] = useState(null);

    const [clients, setClients] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [clientsLoaded, setClientsLoaded] = useState(false);

    // États pour la libération de cache
    const [showLibererModal, setShowLibererModal] = useState(false);
    const [libererData, setLibererData] = useState({ evaluationId: null, type: 'classic', etudiantId: '' });

    // ============================================================
    // RÉCUPÉRATION DES DONNÉES
    // ============================================================

    const fetchClients = useCallback(async () => {
        if (!token) return;
        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/clients?page=0&size=100`, {
                headers: { "Authorization": `Bearer ${token}`, "Accept": "application/json" }
            });
            if (response.ok) {
                const data = await response.json();
                const list = data.data || data.content || data || [];
                setClients(list);
                if (list.length > 0 && !selectedClientId) {
                    setSelectedClientId(list[0].id);
                }
                setClientsLoaded(true);
            } else {
                setClientsLoaded(true);
            }
        } catch (err) {
            console.error("Erreur récupération clients:", err);
            setClientsLoaded(true);
        }
    }, [token, selectedClientId]);

    const fetchClassicEvaluations = useCallback(async (page = 0) => {
        if (!token) return;
        try {
            let url = `${APP_CONFIG.BASE_URL}/api/v1/admin/evaluations?page=${page}&size=${classicPagination.size}`;
            if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
            });
            if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
            const data = await response.json();
            let list = [];
            let pageInfo = {};
            if (data.content) {
                list = data.content;
                pageInfo = { page: data.number || 0, size: data.size || classicPagination.size, totalElements: data.totalElements || 0, totalPages: data.totalPages || 0 };
            } else if (Array.isArray(data)) {
                list = data;
                pageInfo = { page: 0, size: list.length, totalElements: list.length, totalPages: 1 };
            } else if (data.status === 'SUCCESS' && data.data) {
                list = data.data;
                pageInfo = { page: data.page || 0, size: data.size || classicPagination.size, totalElements: data.totalElements || list.length, totalPages: data.totalPages || 1 };
            }
            setClassicEvaluations(list);
            setClassicPagination(prev => ({ ...prev, ...pageInfo, page }));
        } catch (err) {
            console.error("Erreur chargement classiques:", err);
            setError(err.message);
        }
    }, [token, classicPagination.size, searchTerm]);

    const fetchRichEvaluations = useCallback(async (page = 0) => {
        if (!token) return;
        try {
            let url = `${APP_CONFIG.BASE_URL}/api/v1/admin/evaluations-rich?page=${page}&size=${richPagination.size}`;
            if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
            });
            if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
            const data = await response.json();
            let list = [];
            let pageInfo = {};
            if (data.content) {
                list = data.content;
                pageInfo = { page: data.number || 0, size: data.size || richPagination.size, totalElements: data.totalElements || 0, totalPages: data.totalPages || 0 };
            } else if (Array.isArray(data)) {
                list = data;
                pageInfo = { page: 0, size: list.length, totalElements: list.length, totalPages: 1 };
            } else if (data.status === 'SUCCESS' && data.data) {
                list = data.data;
                pageInfo = { page: data.page || 0, size: data.size || richPagination.size, totalElements: data.totalElements || list.length, totalPages: data.totalPages || 1 };
            }
            setRichEvaluations(list);
            setRichPagination(prev => ({ ...prev, ...pageInfo, page }));
        } catch (err) {
            console.error("Erreur chargement enrichies:", err);
            setError(err.message);
        }
    }, [token, richPagination.size, searchTerm]);

    const fetchAllEvaluations = useCallback(async () => {
        setLoading(true);
        setError(null);
        await Promise.all([
            fetchClassicEvaluations(classicPagination.page),
            fetchRichEvaluations(richPagination.page)
        ]);
        setLoading(false);
    }, [fetchClassicEvaluations, fetchRichEvaluations, classicPagination.page, richPagination.page]);

    useEffect(() => {
        fetchClients();
        fetchAllEvaluations();
    }, []);

    useEffect(() => {
        fetchClassicEvaluations(0);
        fetchRichEvaluations(0);
    }, [searchTerm]);

    // ============================================================
    // ACTIONS
    // ============================================================

    const getCurrentList = () => currentTab === 'classic' ? classicEvaluations : richEvaluations;
    const getCurrentPagination = () => currentTab === 'classic' ? classicPagination : richPagination;

    const goToPage = (newPage) => {
        const pag = getCurrentPagination();
        if (newPage >= 0 && newPage < pag.totalPages) {
            if (currentTab === 'classic') {
                fetchClassicEvaluations(newPage);
            } else {
                fetchRichEvaluations(newPage);
            }
        }
    };

    const getClientName = (clientId) => {
        if (!clientId) return 'N/A';
        const client = clients.find(c => c.id === clientId);
        return client ? client.nomEntreprise : 'N/A';
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setSearchTerm(searchInput);
    };

    const handleCreate = (type) => {
        if (!selectedClientId) {
            alert('Veuillez sélectionner un client avant de créer une évaluation.');
            return;
        }
        setSelectedEvaluationId(null);
        setModalMode('create');
        setModalType(type);
        setShowModal(true);
    };

    const handleEdit = (id, type) => {
        const list = type === 'classic' ? classicEvaluations : richEvaluations;
        const evaluation = list.find(ev => ev.id === id);
        if (evaluation && evaluation.clientId) {
            setSelectedClientId(evaluation.clientId);
        } else {
            if (clients.length > 0) setSelectedClientId(clients[0].id);
            else {
                alert('Aucun client disponible. Veuillez en créer un d\'abord.');
                return;
            }
        }
        setSelectedEvaluationId(id);
        setModalMode('edit');
        setModalType(type);
        setShowModal(true);
    };

    const handleDelete = async (id, type) => {
        if (!window.confirm(`Supprimer définitivement cette évaluation ${type === 'classic' ? 'classique' : 'enrichie'} ?`)) return;
        const endpoint = type === 'classic' ? 'evaluations' : 'evaluations-rich';
        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/${endpoint}/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || `Erreur HTTP ${response.status}`);
            }
            await fetchAllEvaluations();
        } catch (err) {
            alert(`Erreur: ${err.message}`);
        }
    };

    const handleToggleStatus = async (id, currentStatus, type) => {
        const newStatus = currentStatus === 'PUBLIEE' ? 'BROUILLON' : 'PUBLIEE';
        if (!window.confirm(`Passer cette évaluation en statut "${newStatus}" ?`)) return;
        const endpoint = type === 'classic' ? 'evaluations' : 'evaluations-rich';
        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/${endpoint}/${id}/status?status=${newStatus}`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || `Erreur HTTP ${response.status}`);
            }
            await fetchAllEvaluations();
        } catch (err) {
            alert(`Erreur: ${err.message}`);
        }
    };

    // ---- Libération du cache ----
    const handleLibererCache = (evaluationId, type) => {
        setLibererData({ evaluationId, type, etudiantId: '' });
        setShowLibererModal(true);
    };

    const confirmLibererCache = async () => {
        const { evaluationId, type, etudiantId } = libererData;
        if (!etudiantId || isNaN(etudiantId)) {
            alert('Veuillez saisir un ID d\'étudiant valide (nombre).');
            return;
        }
        const endpoint = type === 'classic' ? 'evaluations' : 'evaluations-rich';
        try {
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/admin/${endpoint}/${evaluationId}/liberer-cache?etudiantId=${etudiantId}`,
                {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || `Erreur HTTP ${response.status}`);
            }
            const data = await response.json();
            alert(data.message || 'Cache libéré avec succès.');
            setShowLibererModal(false);
        } catch (err) {
            alert(`Erreur: ${err.message}`);
        }
    };

    // ---- Modale de libération ----
    const LibererModal = () => (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-bold text-gray-800">Libérer le cache</h3>
                    <button onClick={() => setShowLibererModal(false)} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">✕</button>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                    Entrez l'ID de l'étudiant pour lequel vous souhaitez libérer le cache de l'évaluation.
                </p>
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">ID de l'étudiant</label>
                    <input
                        type="number"
                        value={libererData.etudiantId}
                        onChange={(e) => setLibererData({ ...libererData, etudiantId: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Ex: 123"
                    />
                </div>
                <div className="flex justify-end gap-2">
                    <button
                        onClick={() => setShowLibererModal(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={confirmLibererCache}
                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                    >
                        Libérer
                    </button>
                </div>
            </div>
        </div>
    );

    // ---- Validation du modèle des questions ----
    const validateEvaluationQuestions = async (evaluation, type) => {
        try {
            const url = type === 'classic' 
                ? `${APP_CONFIG.BASE_URL}/api/v1/admin/evaluations/${evaluation.id}`
                : `${APP_CONFIG.BASE_URL}/api/v1/admin/evaluations-rich/${evaluation.id}`;
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
            });
            if (!response.ok) throw new Error('Erreur chargement');
            const data = await response.json();
            const questions = data.questions || [];

            let errors = [];
            let warnings = [];

            questions.forEach((q, idx) => {
                const num = idx + 1;
                if (!q.enonce || q.enonce.trim() === '') {
                    errors.push(`Question ${num} : énoncé manquant`);
                }
                const typeQ = q.typeQuestion || q.type;
                switch (typeQ) {
                    case 'CHOIX_UNIQUE':
                    case 'CHOIX_MULTIPLE':
                        if (!q.options || q.options.length < 2) {
                            errors.push(`Question ${num} : doit avoir au moins 2 options`);
                        }
                        break;
                    case 'VRAI_FAUX':
                        if (!q.options || q.options.length !== 2) {
                            errors.push(`Question ${num} : doit avoir exactement 2 options (Vrai/Faux)`);
                        }
                        break;
                    case 'APPARIE':
                        if (!q.configurationAvancee || Object.keys(q.configurationAvancee).length === 0) {
                            errors.push(`Question ${num} : configurationAvancee (paires clé/valeur) manquante`);
                        }
                        if (!q.options || q.options.length === 0) {
                            warnings.push(`Question ${num} : options manquantes pour l'affichage des clés`);
                        }
                        break;
                    case 'TEXTE_TROU':
                    case 'REP_COURT':
                    case 'REP_LONG':
                        break;
                    case 'CLASSE':
                        if (!q.configurationAvancee || typeof q.configurationAvancee !== 'object') {
                            errors.push(`Question ${num} : configurationAvancee (catégories) manquante`);
                        } else {
                            const categories = Object.keys(q.configurationAvancee);
                            if (categories.length < 2) {
                                errors.push(`Question ${num} : doit avoir au moins 2 catégories`);
                            }
                            for (const cat of categories) {
                                if (!Array.isArray(q.configurationAvancee[cat]) || q.configurationAvancee[cat].length === 0) {
                                    errors.push(`Question ${num} : la catégorie "${cat}" n'a pas d'éléments`);
                                }
                            }
                        }
                        break;
                    case 'CATEGORI':
                        if (!q.configurationAvancee || typeof q.configurationAvancee !== 'object') {
                            errors.push(`Question ${num} : configurationAvancee (association élément → catégorie) manquante`);
                        } else if (Object.keys(q.configurationAvancee).length < 2) {
                            errors.push(`Question ${num} : doit contenir au moins 2 associations`);
                        }
                        break;
                    case 'AUDIO/FILE':
                        if (q.configurationAvancee) {
                            if (q.configurationAvancee.maxFileSize && isNaN(q.configurationAvancee.maxFileSize)) {
                                warnings.push(`Question ${num} : maxFileSize doit être un nombre`);
                            }
                            if (q.configurationAvancee.acceptedTypes && !Array.isArray(q.configurationAvancee.acceptedTypes)) {
                                warnings.push(`Question ${num} : acceptedTypes doit être un tableau`);
                            }
                        }
                        break;
						case 'APPARIEMENT':
						    // Vérifier que appariement est non vide
						    if (!q.appariement || Object.keys(q.appariement).length === 0) {
						        errors.push(`Question ${num} : appariement (paires clé/valeur) manquant`);
						    }
						    break;

						case 'CLASSEMENT':
						    if (!q.classement || typeof q.classement !== 'object') {
						        errors.push(`Question ${num} : classement (catégories) manquant`);
						    } else {
						        const cats = Object.keys(q.classement);
						        if (cats.length < 2) {
						            errors.push(`Question ${num} : doit avoir au moins 2 catégories`);
						        }
						        for (const cat of cats) {
						            if (!Array.isArray(q.classement[cat]) || q.classement[cat].length === 0) {
						                errors.push(`Question ${num} : la catégorie "${cat}" n'a pas d'éléments`);
						            }
						        }
						    }
						    break;

						case 'ORDONNANCEMENT':
						    if (!q.ordreAttendu || !Array.isArray(q.ordreAttendu) || q.ordreAttendu.length < 2) {
						        errors.push(`Question ${num} : ordre attendu (au moins 2 éléments) manquant`);
						    }
						    break;

						case 'REP_NUMERIQUE':
						    if (q.reponseAttendue === undefined || q.reponseAttendue === '' || isNaN(Number(q.reponseAttendue))) {
						        errors.push(`Question ${num} : réponse numérique attendue invalide`);
						    }
						    break;

						case 'CODE':
						    if (q.feedbackAuto && (!q.reponseAttendue || !q.reponseAttendue.trim())) {
						        warnings.push(`Question ${num} : correction automatique activée mais aucun code attendu défini`);
						    }
						    break;
                   default:
                        errors.push(`Question ${num} : type inconnu (${typeQ})`);
                }
            });

            let message = '';
            if (errors.length === 0 && warnings.length === 0) {
                message = '✅ Toutes les questions sont valides.';
            } else {
                if (errors.length > 0) message += '❌ Erreurs :\n' + errors.join('\n') + '\n';
                if (warnings.length > 0) message += '⚠️ Avertissements :\n' + warnings.join('\n');
            }
            alert(message);
        } catch (err) {
            alert('Erreur de validation : ' + err.message);
        }
    };

    // ---- Rendu tableau ----
    const renderTable = (list, type) => {
        const pag = type === 'classic' ? classicPagination : richPagination;
        if (loading) return <div className="flex justify-center py-6"><div className="animate-spin h-6 w-6 border-b-2 border-green-600"></div></div>;
        if (error) return <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg text-sm">{error} <button onClick={fetchAllEvaluations} className="ml-2 text-green-600 underline font-medium">Réessayer</button></div>;
        if (list.length === 0) {
            return <div className="text-center py-6 text-gray-500 text-sm">{searchTerm ? 'Aucune évaluation ne correspond' : `Aucune évaluation ${type === 'classic' ? 'classique' : 'enrichie'} disponible`}</div>;
        }
        return (
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                            <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titre</th>
                            <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                            <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                            <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                            <th className="px-3 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Questions</th>
                            <th className="px-3 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Score min</th>
                            <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rattachement</th>
                            <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {list.map(ev => {
                            const hasQuestions = ev.questions && ev.questions.length > 0;
                            const isIntegrated = ev.moduleId || ev.formationId;
                            return (
                                <tr key={ev.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-3 py-1.5 whitespace-nowrap text-xs font-mono text-gray-600">{ev.code || '-'}</td>
                                    <td className="px-3 py-1.5 text-xs text-gray-800"><div className="max-w-xs truncate" title={ev.titre}>{ev.titre || 'Sans titre'}</div></td>
                                    <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-600">{getClientName(ev.clientId)}</td>
                                    <td className="px-3 py-1.5 whitespace-nowrap text-xs">
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${ev.type === 'DIAGNOSTIC' ? 'bg-red-100 text-red-700' : ev.type === 'FORMATIVE' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'}`}>
                                            {ev.type || 'SOMMATIVE'}
                                        </span>
                                    </td>
                                    <td className="px-3 py-1.5 whitespace-nowrap text-xs">
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${ev.status === 'PUBLIEE' ? 'bg-emerald-100 text-emerald-700' : ev.status === 'TERMINEE' ? 'bg-gray-100 text-gray-700' : 'bg-amber-100 text-amber-700'}`}>
                                            {ev.status || 'BROUILLON'}
                                        </span>
                                    </td>
                                    <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-600 text-center">{ev.nombreQuestions ?? ev.questions?.length ?? 0}</td>
                                    <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-600 text-center">{ev.scoreMinimum ?? 70}%</td>
                                    <td className="px-3 py-1.5 whitespace-nowrap text-xs">
                                        {isIntegrated ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800">
                                                Intégrée {ev.moduleId ? `(module ${ev.moduleId})` : ev.formationId ? `(formation ${ev.formationId})` : ''}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                                                Indépendante
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-1.5 whitespace-nowrap text-right text-xs">
                                        <div className="flex items-center justify-end gap-0.5 flex-wrap">
                                            {hasQuestions && (
                                                <>
                                                    <button onClick={() => handleOpenDemo(ev, type)} className="p-1 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded-lg transition-colors" title="Prévisualiser"><Eye size={15} /></button>
                                                    <button onClick={() => validateEvaluationQuestions(ev, type)} className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors" title="Valider le modèle des questions">
                                                        <CheckCircle size={15} />
                                                    </button>
                                                </>
                                            )}
                                            <button onClick={() => handleToggleStatus(ev.id, ev.status, type)} className={`text-[9px] px-2 py-0.5 rounded-md transition-colors ${ev.status === 'PUBLIEE' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                                                {ev.status === 'PUBLIEE' ? 'Dépublier' : 'Publier'}
                                            </button>
                                            <button onClick={() => handleEdit(ev.id, type)} className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors" title="Modifier"><Edit size={15} /></button>
                                            <button onClick={() => handleDelete(ev.id, type)} className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors" title="Supprimer"><Trash2 size={15} /></button>
                                            <button onClick={() => handleLibererCache(ev.id, type)} className="p-1 text-amber-600 hover:text-amber-800 hover:bg-amber-50 rounded-lg transition-colors" title="Libérer le cache pour un étudiant"><Clock size={15} /></button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {pag.totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-3 px-4 py-3 bg-gray-50 border-t border-gray-200">
                        <span className="text-xs text-gray-500">
                            {pag.totalElements} évaluation(s) - Page {pag.page + 1}/{pag.totalPages}
                        </span>
                        <div className="flex gap-1.5">
                            <button
                                onClick={() => goToPage(pag.page - 1)}
                                disabled={pag.page === 0}
                                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                ← Précédent
                            </button>
                            <button
                                onClick={() => goToPage(pag.page + 1)}
                                disabled={pag.page + 1 >= pag.totalPages}
                                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Suivant →
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const handleOpenDemo = async (evaluation, type) => {
        if (!evaluation) {
            alert("❌ Informations de l'évaluation manquantes");
            return;
        }
        const questions = evaluation.questions || [];
        if (questions.length === 0) {
            alert("❌ Cette évaluation ne contient aucune question");
            return;
        }
        try {
            const isRich = type === 'rich';
            const demoData = {
                isDirectEvaluation: true,
                isEvaluationRich: isRich,
                formationId: null,
                module: {
                    id: evaluation.id,
                    titre: evaluation.titre || 'Évaluation sans titre',
                    description: evaluation.description || '',
                    type: isRich ? 'EVALUATION_RICH' : 'EVALUATION',
                    ordre: 0,
                    formationId: null,
                    evalType: evaluation.type || 'SOMMATIVE',
                    typeModule: isRich ? 'EVALUATION_RICH' : 'EVALUATION'
                },
                content: {
                    type: isRich ? 'EVALUATION_RICH' : 'EVALUATION',
                    data: {
                        id: evaluation.id,
                        titre: evaluation.titre,
                        description: evaluation.description,
                        type: evaluation.type,
                        questions: questions,
                        scoreMinimum: evaluation.scoreMinimum || 70,
                        isRich: isRich
                    }
                },
                token: token,
                mode: 'demo'
            };
            setDemoEvalData(demoData);
            setShowEvalDemo(true);
        } catch (err) {
            console.error("❌ Erreur ouverture démo évaluation:", err);
            alert("❌ Erreur lors de l'ouverture de la démo: " + err.message);
        }
    };

    const handleModalClose = () => {
        setShowModal(false);
        setSelectedEvaluationId(null);
        fetchAllEvaluations();
    };

    // ============================================================
    // RENDU PRINCIPAL
    // ============================================================

    return (
        <div className="space-y-3 p-4 w-[70%] max-w-full mx-auto">
            {/* En-tête avec onglets */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-gray-800">Gestion des évaluations</h2>
                    <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                        <button
                            onClick={() => setCurrentTab('classic')}
                            className={`px-3 py-1.5 text-xs font-medium transition-colors ${currentTab === 'classic' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                        >
                            <FileQuestion size={14} className="inline mr-1" /> Classiques
                        </button>
                        <button
                            onClick={() => setCurrentTab('rich')}
                            className={`px-3 py-1.5 text-xs font-medium transition-colors ${currentTab === 'rich' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                        >
                            <Layers size={14} className="inline mr-1" /> Enrichies
                        </button>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <form onSubmit={handleSearch} className="flex gap-2">
                        <input
                            type="text"
                            placeholder="Rechercher..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="pl-3 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent w-40"
                        />
                        <button type="submit" className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors">
                            <Search size={14} />
                        </button>
                    </form>
                    <button
                        onClick={() => handleCreate(currentTab === 'classic' ? 'classic' : 'rich')}
                        disabled={!clientsLoaded || !selectedClientId}
                        className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
                    >
                        <Plus size={16} /> Créer {currentTab === 'classic' ? 'classique' : 'enrichie'}
                    </button>
                    <button
                        onClick={fetchAllEvaluations}
                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Rafraîchir"
                    >
                        <RefreshCw size={16} />
                    </button>
                </div>
            </div>

            {/* Tableau */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3">
                {renderTable(getCurrentList(), currentTab)}
            </div>

            {/* Modal de création/édition */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-5">
                        <div className="flex justify-between items-start mb-3">
                            <h3 className="text-lg font-bold text-gray-800">
                                {modalMode === 'create'
                                    ? `Nouvelle évaluation ${modalType === 'classic' ? 'classique' : 'enrichie'}`
                                    : `Modifier l'évaluation ${modalType === 'classic' ? 'classique' : 'enrichie'}`
                                }
                            </h3>
                            <button onClick={handleModalClose} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">✕</button>
                        </div>
                        <div className="mb-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Client * (rattachement)</label>
                            <select
                                value={selectedClientId || ''}
                                onChange={(e) => setSelectedClientId(Number(e.target.value))}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                required
                            >
                                <option value="">Sélectionnez un client</option>
                                {clients.map(client => (
                                    <option key={client.id} value={client.id}>{client.nomEntreprise} ({client.code})</option>
                                ))}
                            </select>
                            <p className="text-xs text-gray-400 mt-1">L'évaluation sera rattachée à ce client.</p>
                        </div>
                        {modalType === 'classic' ? (
                            <ConstruireEvaluation
                                evaluationId={selectedEvaluationId}
                                clientId={selectedClientId}
                                token={token}
                                evalType="SOMMATIVE"
                                onSave={handleModalClose}
                                onDelete={handleModalClose}
                                onPreview={(data) => console.log('Prévisualisation classique', data)}
                            />
                        ) : (
                            <ConstruireEvaluationRich
                                evaluationId={selectedEvaluationId}
                                clientId={selectedClientId}
                                token={token}
                                onSave={handleModalClose}
                                onDelete={handleModalClose}
                                onPreview={(data) => console.log('Prévisualisation enrichie', data)}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Démo d'évaluation */}
            {showEvalDemo && demoEvalData && (
                <DemoViewer
                    formationId={null}
                    token={demoEvalData.token || token}
                    onClose={() => { setShowEvalDemo(false); setDemoEvalData(null); }}
                    isOpen={showEvalDemo}
                    userRole="admin"
                    initialModule={demoEvalData.module}
                    initialContent={demoEvalData.content}
                    isDirectEvaluation={demoEvalData.isDirectEvaluation}
                    isEvaluationRich={demoEvalData.isEvaluationRich}
                />
            )}

            {/* Modale de libération de cache */}
            {showLibererModal && <LibererModal />}
        </div>
    );
}